import { useCallback, useState } from "react";

import { notify } from "../../../../components/Notification";
import {
  downloadCustomRangeReportPdf,
  downloadTaxYearReportPdf,
  ErrorResponse,
  getErrorMessages,
  ReportResponse,
  useLazyGetCustomRangeReportQuery,
  useLazyGetTaxYearReportQuery,
} from "../../../../services";
import { GenerateReportParams, UseReportsDataResult } from "./types";

export const useReportsData = (): UseReportsDataResult => {
  const [triggerCustomRange, customRangeState] =
    useLazyGetCustomRangeReportQuery();
  const [triggerTaxYear, taxYearState] = useLazyGetTaxYearReportQuery();

  const [report, setReport] = useState<ReportResponse>();
  const [isDownloading, setIsDownloading] = useState(false);
  // The params that produced the current view, so retry re-runs what was submitted
  // rather than whatever the form happens to hold now.
  const [lastParams, setLastParams] = useState<GenerateReportParams>();

  const isGenerating = customRangeState.isFetching || taxYearState.isFetching;
  // Each lazy query preserves its own result. Only the query that produced the
  // current view may control the error state; otherwise a failed tax-year
  // request would mask a later successful custom-range report (and vice versa).
  const activeError =
    lastParams?.mode === "taxYear"
      ? taxYearState.error
      : customRangeState.error;

  /**
   * `axiosBaseQuery` surfaces whatever the interceptor threw — the API's own
   * `{ errors: [...] }` body, not RTK Query's `{ status, data }`. `ErrorState` needs a
   * string, and this is what puts the server's "Available tax years: X-Y" message on
   * screen verbatim.
   */
  let errorMessage: string | undefined;
  if (activeError) {
    errorMessage = getErrorMessages(activeError as ErrorResponse).join(" ");
  }

  const generateReport = useCallback(
    async (params: GenerateReportParams) => {
      setLastParams(params);
      setReport(undefined);

      if (params.mode === "taxYear" && params.taxYear !== undefined) {
        const result = await triggerTaxYear({ taxYear: params.taxYear });
        setReport(result.data);
        return;
      }

      if (params.startDate && params.endDate) {
        const result = await triggerCustomRange({
          startDate: params.startDate,
          endDate: params.endDate,
        });
        setReport(result.data);
      }
    },
    [triggerCustomRange, triggerTaxYear],
  );

  const retry = useCallback(async () => {
    if (lastParams) {
      await generateReport(lastParams);
    }
  }, [generateReport, lastParams]);

  /**
   * A tax-year report downloads through the dedicated tax-year PDF endpoint so the file carries
   * the returns & activity section; the server resolves the year to its own calendar range. A
   * custom-range report downloads with the period the server resolved. Discriminated on the
   * presence of `returnsActivity`, which only the tax-year response carries.
   */
  const downloadPdf = useCallback(async () => {
    if (!report) {
      return;
    }

    setIsDownloading(true);
    try {
      if ("returnsActivity" in report) {
        await downloadTaxYearReportPdf({ taxYear: report.period.taxYear });
      } else {
        await downloadCustomRangeReportPdf({
          startDate: report.period.startDate,
          endDate: report.period.endDate,
        });
      }
      notify.success("Report downloaded successfully!");
    } catch (err: unknown) {
      // The PDF download bypasses RTK Query, so errorToastMiddleware never sees it.
      notify.apiError(err as ErrorResponse);
    } finally {
      setIsDownloading(false);
    }
  }, [report]);

  return {
    report,
    isGenerating,
    isDownloading,
    errorMessage,
    generateReport,
    retry,
    downloadPdf,
  };
};
